import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Clock, CheckCircle, Play, Navigation, Map, Truck, AlertCircle } from 'lucide-react';
import { FaseOS, OrdemServico } from '../types';
import { useOSStore } from '../stores/osStore';
import { useAuthStore } from '../stores/authStore';
import MapComponent from './MapComponent';
import { geocodeAddress } from '../utils/geocoding';
import { api } from '../utils/api';
import socketService from '../services/socketService';
import { useLocationTracking } from '../hooks/useLocationTracking';
import { askToUseNativeApp, openNativeApp } from '../utils/nativeApp';

interface FaseCardProps {
  fase: FaseOS;
  ordemServico: OrdemServico;
}

const FaseCard: React.FC<FaseCardProps> = ({ fase, ordemServico }) => {
  const { updateFaseStatus } = useOSStore();
  const { token } = useAuthStore();
  const [showMap, setShowMap] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [destinoCoords, setDestinoCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Inicializar shouldTrack baseado no status da fase
  const [shouldTrack, setShouldTrack] = useState(() => fase.status_fase === 'Em Rota');
  const [routeInfo, setRouteInfo] = useState<{ distance: number; duration: number; instructions: string[] } | null>(null);
  
  // Sincronizar shouldTrack quando o status da fase mudar
  useEffect(() => {
    const isEmRota = fase.status_fase === 'Em Rota';
    
    setShouldTrack(prevShouldTrack => {
      // Só atualizar se realmente mudou
      if (isEmRota && !prevShouldTrack) {
        console.log('🚀 Fase mudou para "Em Rota", ativando rastreamento...');
        
        // Oferecer opção de usar app nativo (opcional, não bloqueia)
        if (token) {
          // Perguntar de forma não intrusiva - apenas uma vez por sessão
          const hasAskedThisSession = sessionStorage.getItem(`native-app-asked-${ordemServico.id}-${fase.id}`);
          if (!hasAskedThisSession) {
            setTimeout(() => {
              askToUseNativeApp(
                {
                  osId: ordemServico.id,
                  faseId: fase.id,
                  token: token,
                  action: 'start',
                },
                () => {
                  // Se cancelar ou app não disponível, continua com rastreamento web normal
                  console.log('Continuando com rastreamento web');
                }
              );
              sessionStorage.setItem(`native-app-asked-${ordemServico.id}-${fase.id}`, 'true');
            }, 500); // Pequeno delay para não interromper o fluxo
          }
        }
        
        return true;
      } else if (!isEmRota && prevShouldTrack) {
        console.log('🛑 Fase saiu de "Em Rota", parando rastreamento...');
        
        // Tentar parar rastreamento no app nativo também (se estiver usando)
        if (token) {
          try {
            openNativeApp({
              osId: ordemServico.id,
              faseId: fase.id,
              token: token,
              action: 'stop',
            });
          } catch (error) {
            // Ignorar erro - app nativo pode não estar instalado
            console.log('App nativo não disponível ou erro ao parar');
          }
        }
        
        return false;
      }
      return prevShouldTrack;
    });
  }, [fase.status_fase, token, ordemServico.id, fase.id]);

  // Tentar reativar app nativo periodicamente enquanto estiver em rota
  useEffect(() => {
    if (fase.status_fase !== 'Em Rota' || !token) {
      return;
    }

    // Marcar que estamos tentando usar app nativo
    const nativeAppKey = `native-app-using-${ordemServico.id}-${fase.id}`;
    const isUsingNative = sessionStorage.getItem(nativeAppKey) === 'true';

    if (!isUsingNative) {
      // Se não está usando, verificar periodicamente se o usuário quer usar
      const checkInterval = setInterval(() => {
        const hasAsked = sessionStorage.getItem(`native-app-asked-${ordemServico.id}-${fase.id}`);
        if (!hasAsked) {
          // Se ainda não perguntou nesta sessão, perguntar novamente
          askToUseNativeApp(
            {
              osId: ordemServico.id,
              faseId: fase.id,
              token: token,
              action: 'start',
            },
            () => {
              console.log('Usuário escolheu continuar com web');
            }
          );
        }
      }, 60000); // A cada 1 minuto

      return () => clearInterval(checkInterval);
    } else {
      // Se está usando app nativo, tentar reativá-lo periodicamente (a cada 30 segundos)
      // para garantir que continue rastreando mesmo se perder comunicação
      const reactivateInterval = setInterval(() => {
        console.log('🔄 Verificando e reativando app nativo...');
        try {
          openNativeApp({
            osId: ordemServico.id,
            faseId: fase.id,
            token: token,
            action: 'start',
          });
        } catch (error) {
          console.log('Erro ao reativar app nativo (pode não estar instalado):', error);
        }
      }, 30000); // A cada 30 segundos

      return () => clearInterval(reactivateInterval);
    }
  }, [fase.status_fase, token, ordemServico.id, fase.id]);
  
  // Conectar WebSocket quando necessário
  useEffect(() => {
    if (shouldTrack && token) {
      socketService.connect(token)
        .then(() => {
          console.log('✅ WebSocket conectado!');
          socketService.joinOSRoom(ordemServico.codigo_acesso);
        })
        .catch((error) => {
          console.error('❌ Erro ao conectar WebSocket:', error);
        });
    }
  }, [shouldTrack, token, ordemServico.codigo_acesso]);
  
  // Usar novo hook de rastreamento com suporte a background
  const {
    isTracking,
    lastSentLocation,
    error: trackingError,
    syncPendingLocations
  } = useLocationTracking({
    osId: ordemServico.id,
    faseId: fase.id,
    enabled: shouldTrack && fase.status_fase === 'Em Rota',
    interval: 15000, // 15 segundos
    onLocationUpdate: (location) => {
      setCurrentLocation({ lat: location.lat, lng: location.lng });
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendente': return 'text-yellow-600 bg-yellow-50';
      case 'Em Rota': return 'text-blue-600 bg-blue-50';
      case 'Concluída': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pendente': return <Clock className="h-5 w-5" />;
      case 'Em Rota': return <Navigation className="h-5 w-5" />;
      case 'Concluída': return <CheckCircle className="h-5 w-5" />;
      default: return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getFaseIcon = (nomeFase: string) => {
    switch (nomeFase.toLowerCase()) {
      case 'remoção': return <Truck className="h-5 w-5" />;
      case 'clínica': return <MapPin className="h-5 w-5" />;
      case 'velório': return <Clock className="h-5 w-5" />;
      case 'sepultamento': return <CheckCircle className="h-5 w-5" />;
      default: return <MapPin className="h-5 w-5" />;
    }
  };

  const getCurrentLocation = (): Promise<{ 
    lat: number; 
    lng: number; 
    velocidade?: number; 
    direcao?: number; 
    precisao?: number; 
    altitude?: number; 
  }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não suportada'));
        return;
      }

      // Timeout mais longo e opções mais flexíveis
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            velocidade: position.coords.speed || undefined,
            direcao: position.coords.heading || undefined,
            precisao: position.coords.accuracy || undefined,
            altitude: position.coords.altitude || undefined
          });
        },
        (error) => {
          console.error('Erro no getCurrentLocation:', error);
          // Tentar novamente com configurações mais permissivas
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                velocidade: position.coords.speed || undefined,
                direcao: position.coords.heading || undefined,
                precisao: position.coords.accuracy || undefined,
                altitude: position.coords.altitude || undefined
              });
            },
            (retryError) => {
              reject(retryError);
            },
            {
              enableHighAccuracy: false, // Tentar sem alta precisão
              timeout: 15000, // 15 segundos
              maximumAge: 60000 // Aceitar cache de até 1 minuto
            }
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 20000, // Aumentado para 20 segundos
          maximumAge: 60000 // Aceitar cache de até 1 minuto
        }
      );
    });
  };

  const handleGeocodeAddress = async (endereco: string) => {
    try {
      const coords = await geocodeAddress(endereco);
      setDestinoCoords(coords);
      return coords;
    } catch (error) {
      console.error('Erro na geocodificação:', error);
      // Usar coordenadas padrão em caso de erro
      const fallbackCoords = { lat: -23.5505, lng: -46.6333 };
      setDestinoCoords(fallbackCoords);
      return fallbackCoords;
    }
  };

  const handleShowMap = async () => {
    try {
      // Se já temos localização do rastreamento, usar ela
      if (currentLocation) {
        console.log('📍 Usando localização do rastreamento:', currentLocation);
        await handleGeocodeAddress(fase.endereco_destino);
        setShowMap(true);
        return;
      }

      // Tentar obter localização atual apenas se não está rastreando
      // (se está rastreando, o watchPosition já vai fornecer a localização)
      if (!isTracking) {
        console.log('📍 Tentando obter localização atual...');
        try {
          const location = await getCurrentLocation();
          setCurrentLocation(location);
        } catch (error) {
          console.warn('⚠️ Não foi possível obter localização inicial, aguardando do rastreamento...', error);
          // Usar coordenadas padrão temporariamente
          setCurrentLocation({ lat: -23.5505, lng: -46.6333 });
        }
      } else {
        // Se está rastreando, usar coordenadas padrão temporariamente
        // A localização será atualizada pelo watchPosition
        if (!currentLocation) {
          setCurrentLocation({ lat: -23.5505, lng: -46.6333 });
        }
      }
      
      // Geocodificar endereço de destino e mostrar mapa
      await handleGeocodeAddress(fase.endereco_destino);
      setShowMap(true);
    } catch (error) {
      console.error('Erro ao preparar mapa:', error);
      // Mesmo com erro, mostrar mapa com coordenadas padrão
      if (!currentLocation) {
        setCurrentLocation({ lat: -23.5505, lng: -46.6333 });
      }
      await handleGeocodeAddress(fase.endereco_destino);
      setShowMap(true);
    }
  };

  const handleIniciarProcesso = async () => {
    try {
      setIsProcessing(true);
      
      // Atualizar status da fase para "Em Rota"
      await updateFaseStatus(fase.id, 'Em Rota');
      
      // Iniciar rastreamento automaticamente
      setShouldTrack(true);
      
      // Oferecer app nativo imediatamente ao iniciar processo
      if (token) {
        // Limpar flag de sessão para permitir perguntar novamente
        sessionStorage.removeItem(`native-app-asked-${ordemServico.id}-${fase.id}`);
        
        // Pequeno delay para não interromper o fluxo visual
        setTimeout(() => {
          askToUseNativeApp(
            {
              osId: ordemServico.id,
              faseId: fase.id,
              token: token,
              action: 'start',
            },
            () => {
              console.log('Continuando com rastreamento web');
            }
          );
          sessionStorage.setItem(`native-app-asked-${ordemServico.id}-${fase.id}`, 'true');
        }, 500);
      }
      
    } catch (error) {
      console.error('Erro ao iniciar processo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinalizarProcesso = async () => {
    try {
      setIsProcessing(true);
      
      // Parar rastreamento
      setShouldTrack(false);
      
      // Tentar parar rastreamento no app nativo também (se estiver usando)
      if (token) {
        try {
          console.log('🛑 Finalizando processo - parando app nativo...');
          openNativeApp({
            osId: ordemServico.id,
            faseId: fase.id,
            token: token,
            action: 'stop',
          });
        } catch (error) {
          // Ignorar erro - app nativo pode não estar instalado
          console.log('App nativo não disponível ou erro ao parar');
        }
      }
      
      // Sincronizar localizações pendentes antes de parar
      await syncPendingLocations();
      
      // Atualizar status da fase para "Concluída"
      await updateFaseStatus(fase.id, 'Concluída');
      
    } catch (error) {
      console.error('Erro ao finalizar processo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const canStartProcess = fase.status_fase === 'Pendente';
  const canFinishProcess = fase.status_fase === 'Em Rota';

  return (
    <div className="bg-white rounded-lg shadow-lg border p-6">
      {/* Header da Fase */}
      <div className="flex items-center justify-between mb-4">
                 <div className="flex items-center space-x-3">
           <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
             {getFaseIcon(fase.nome_fase)}
           </div>
           <div>
             <h3 className="text-lg font-semibold text-gray-900">{fase.nome_fase}</h3>
             <p className="text-sm text-gray-500">
               {ordemServico.numero_nota_contratacao ? `N.C. ${ordemServico.numero_nota_contratacao}` : `OS #${ordemServico.codigo_acesso}`}
             </p>
           </div>
         </div>
        
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(fase.status_fase)}`}>
          <div className="flex items-center space-x-2">
            {getStatusIcon(fase.status_fase)}
            <span>{fase.status_fase}</span>
          </div>
        </span>
      </div>

      {/* Informações da Fase */}
      <div className="space-y-4 mb-4">
        <div className="flex items-center space-x-3">
          <MapPin className="h-5 w-5 text-gray-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">Endereço de Destino</p>
            <p className="text-sm text-gray-500">{fase.endereco_destino}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Ordem</p>
              <p className="text-sm text-gray-500">Fase {fase.ordem}</p>
            </div>
          </div>
          
          {(fase.data_fase || fase.horario) && (
            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">Agendamento</p>
                <p className="text-sm text-gray-500">
                  {(() => {
                    const partes = [];
                    if (fase.data_fase) {
                      try {
                        const date = new Date(fase.data_fase);
                        partes.push(date.toLocaleDateString('pt-BR'));
                      } catch {
                        partes.push(fase.data_fase);
                      }
                    }
                    if (fase.horario) {
                      // Formatar horário (pode vir como HH:MM ou HHMM)
                      let horarioFormatado = fase.horario.trim();
                      if (!horarioFormatado.includes(':') && horarioFormatado.length === 4) {
                        horarioFormatado = `${horarioFormatado.substring(0, 2)}:${horarioFormatado.substring(2, 4)}`;
                      }
                      partes.push(horarioFormatado);
                    }
                    return partes.length > 0 ? partes.join(' - ') : 'Não agendado';
                  })()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status do Rastreamento */}
      {isTracking && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-800">
              Rastreamento ativo - Enviando localização...
            </span>
          </div>
        </div>
      )}

      {/* Botões de Ação */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Botão Iniciar Processo */}
        {canStartProcess && (
          <button
            onClick={handleIniciarProcesso}
            disabled={isProcessing}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {isProcessing ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Iniciando...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Play className="h-4 w-4" />
                <span>Iniciar Processo</span>
              </div>
            )}
          </button>
        )}

        {/* Botão Finalizar Processo */}
        {canFinishProcess && (
          <button
            onClick={handleFinalizarProcesso}
            disabled={isProcessing}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isProcessing ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Finalizando...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <CheckCircle className="h-4 w-4" />
                <span>Finalizar Processo</span>
              </div>
            )}
          </button>
        )}

        {/* Botão Mapa */}
        <button
          onClick={handleShowMap}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Map className="h-4 w-4" />
            <span>Mapa</span>
          </div>
        </button>
      </div>

             {/* Modal do Mapa */}
       {showMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Mapa e Rota - {fase.nome_fase}</h3>
              <button
                onClick={() => setShowMap(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="sr-only">Fechar</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <MapComponent
                origem={currentLocation || { lat: -23.5505, lng: -46.6333 }}
                destino={destinoCoords || { lat: -23.5505, lng: -46.6333 }}
                fases={ordemServico.fases}
                showTimeline={true}
                isTracking={isTracking}
                onRouteCalculated={setRouteInfo}
              />
              
              {/* Informações da rota calculada */}
              {routeInfo && (
                <div className="bg-white rounded-lg shadow-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Informações da Rota</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {routeInfo.distance > 0 ? `${routeInfo.distance.toFixed(1)} km` : 'Calculando...'}
                      </div>
                      <div className="text-sm text-blue-600">Distância</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {routeInfo.duration > 0 ? `${Math.round(routeInfo.duration)} min` : 'Calculando...'}
                      </div>
                      <div className="text-sm text-green-600">Tempo Estimado</div>
                    </div>
                  </div>
                  
                  {routeInfo.instructions.length > 0 && (
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <Navigation className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-700">Instruções</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                        {routeInfo.instructions.slice(0, 3).map((instruction, index) => (
                          <div key={index} className="flex items-start space-x-2 mb-2 last:mb-0">
                            <span className="text-xs bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                              {index + 1}
                            </span>
                            <span className="text-sm text-gray-700">{instruction}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaseCard;
